import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  approveApplicant,
  getApplicant,
  rejectApplicant,
  requestApplicantInfo,
  retryApplicantEnrichment,
  revokeApplicant,
} from "./api";
import { ConfidenceBadge, ConfirmModal, ExternalLink, StatusBadge, VerdictBadge, errorMessage, formatTimestamp, safeHttpUrl } from "./shared";

const OUTCOME_LABELS = { reject: "Blocks", review: "Review", info: "Info" };

const WEB_CHECK_STATUS_LABELS = {
  ok: "Completed",
  disabled: "Disabled",
  unavailable: "Unavailable",
  skipped: "Skipped",
  failed: "Failed",
};

const NMLS_STATUS_LABELS = {
  confirmed: "Confirmed",
  not_found: "Not found",
  conflict: "Conflict",
  not_checked: "Not checked",
};

const RELATION_LABELS = {
  current: "Current",
  past: "Past",
  parent: "Parent",
  subsidiary: "Subsidiary",
  similar_name: "Similar name",
};

const ACTIONS = {
  approve: {
    title: "Approve demo access?",
    description: "The applicant can enter the demo on their invite link. Invite expiry and device limits still apply. They are emailed that the demo is ready.",
    confirmLabel: "Approve",
    textLabel: "Internal note (optional)",
    run: (env, id, text) => approveApplicant(env, id, text),
    success: "Applicant approved",
  },
  reject: {
    title: "Reject demo access?",
    description: "The applicant sees a neutral message and cannot enter the demo. Screening details are never shown to them.",
    confirmLabel: "Reject",
    tone: "danger",
    textLabel: "Internal note (optional)",
    run: (env, id, text) => rejectApplicant(env, id, text),
    success: "Applicant rejected",
  },
  revoke: {
    title: "Revoke demo access?",
    description: "Any open demo session ends within five minutes and the applicant cannot enter again.",
    confirmLabel: "Revoke access",
    tone: "danger",
    textLabel: "Internal note (optional)",
    run: (env, id, text) => revokeApplicant(env, id, text),
    success: "Access revoked",
  },
  requestInfo: {
    title: "Request more information",
    description: "The applicant is emailed this message and can update their details on the invite link. Do not mention screening rules or competitors.",
    confirmLabel: "Send request",
    textLabel: "Message to the applicant (required)",
    textPlaceholder: "For example: please use your company email address so we can confirm where you work.",
    textRequired: true,
    run: (env, id, text) => requestApplicantInfo(env, id, text),
    success: "Information requested",
  },
  retry: {
    title: "Re-run the check?",
    description: "Runs the AI web check again for this applicant and re-screens them.",
    confirmLabel: "Re-run check",
    run: (env, id) => retryApplicantEnrichment(env, id),
    success: "Check re-run",
  },
};

function Field({ label, children }) {
  return (
    <div className="da-field">
      <dt>{label}</dt>
      <dd>{children || children === 0 ? children : <span className="ref-muted">—</span>}</dd>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="da-section">
      <h3 className="da-section-title">{title}</h3>
      {children}
    </section>
  );
}

function IdentitySection({ applicant }) {
  const { linkedin = {}, profile } = applicant;

  return (
    <Section title="Identity">
      <p className="ref-muted da-hint">LinkedIn sign-in proves account ownership only, not legal identity or employment.</p>
      <dl className="da-fields">
        <Field label="LinkedIn name">{linkedin.name}</Field>
        <Field label="LinkedIn email">
          {linkedin.email ? `${linkedin.email} (${linkedin.emailVerified ? "verified by LinkedIn" : "not verified by LinkedIn"})` : ""}
        </Field>
        <Field label="LinkedIn subject">{linkedin.sub ? <code>{linkedin.sub}</code> : ""}</Field>
        <Field label="Work email">
          {profile?.workEmail ? `${profile.workEmail} (${applicant.workEmailVerifiedAt ? `verified ${formatTimestamp(applicant.workEmailVerifiedAt)}` : "not verified"})` : ""}
        </Field>
        <Field label="Invite sent to">{applicant.inviteEmail}</Field>
        <Field label="Full name">{profile?.fullName}</Field>
        <Field label="Company">{profile?.companyName}</Field>
        <Field label="Claimed title">{profile?.jobTitle}</Field>
        <Field label="LinkedIn URL (applicant-supplied)">{profile?.linkedinUrl}</Field>
        <Field label="Company website">{profile?.companyWebsite ? <ExternalLink url={profile.companyWebsite} /> : ""}</Field>
        <Field label="NMLS ID">{profile?.nmlsId || (profile?.noNmls ? `No NMLS ID: ${profile.noNmlsReason || "no reason given"}` : "")}</Field>
      </dl>
    </Section>
  );
}

function triStateLabel(value) {
  if (value === true) {
    return "Yes";
  }

  return value === false ? "No" : "";
}

function WebCheckSection({ applicant }) {
  const { webCheck, webCheckStatus } = applicant;

  if (!webCheck) {
    return (
      <Section title="Web check">
        <p className="ref-muted">
          No web check result{webCheckStatus ? ` (${WEB_CHECK_STATUS_LABELS[webCheckStatus] || webCheckStatus})` : ""}. A missing result does not show the applicant has no competitor affiliation.
        </p>
      </Section>
    );
  }

  const { employer, nmls } = webCheck;
  const sources = (webCheck.sources || []).filter((source) => safeHttpUrl(source.url));

  return (
    <Section title="Web check">
      <p className="ref-muted da-hint">The AI web check is a signal from public web sources, not proof of identity or employment.</p>
      <dl className="da-fields">
        <Field label="Verdict">
          <VerdictBadge verdict={webCheck.verdict} />
        </Field>
        <Field label="Confidence">
          <ConfidenceBadge confidence={webCheck.confidence} />
        </Field>
        <Field label="Status">{WEB_CHECK_STATUS_LABELS[webCheck.status] || webCheck.status}</Field>
        <Field label="Person found online">{triStateLabel(webCheck.personFound)}</Field>
        <Field label="Employer">{employer?.name}</Field>
        <Field label="Employer website">{employer?.website ? <ExternalLink url={employer.website} /> : ""}</Field>
        <Field label="Industry">{employer?.industry}</Field>
        <Field label="Mortgage related">{triStateLabel(employer?.isMortgageRelated)}</Field>
        <Field label="NMLS">{nmls ? [NMLS_STATUS_LABELS[nmls.status] || nmls.status, nmls.note].filter(Boolean).join(" · ") : ""}</Field>
        <Field label="Model">{webCheck.model}</Field>
        <Field label="Checked">{formatTimestamp(webCheck.checkedAt)}</Field>
      </dl>
      {webCheck.summary ? <p className="da-evidence">{webCheck.summary}</p> : null}
      {webCheck.redFlags?.length ? (
        <>
          <h4 className="da-subtitle">Red flags</h4>
          <ul className="da-list">
            {webCheck.redFlags.map((flag) => (
              <li key={flag}>{flag}</li>
            ))}
          </ul>
        </>
      ) : null}
      {webCheck.competitorSignals?.length ? (
        <>
          <h4 className="da-subtitle">Competitor signals</h4>
          <ul className="da-list">
            {webCheck.competitorSignals.map((signal) => (
              <li key={`${signal.name}-${signal.relation}`}>
                <span className="ref-org-name">{signal.name}</span> <span className="ref-muted">({RELATION_LABELS[signal.relation] || signal.relation})</span>
                {signal.evidence ? <div className="da-evidence">{signal.evidence}</div> : null}
              </li>
            ))}
          </ul>
        </>
      ) : null}
      {sources.length ? (
        <>
          <h4 className="da-subtitle">Sources</h4>
          <ul className="da-list">
            {sources.map((source) => (
              <li key={source.url}>
                <ExternalLink url={source.url}>{source.title || source.url}</ExternalLink>
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </Section>
  );
}

function ScreeningSection({ screening }) {
  if (!screening) {
    return (
      <Section title="Screening">
        <p className="ref-muted">Not screened yet.</p>
      </Section>
    );
  }

  return (
    <Section title="Screening">
      <dl className="da-fields">
        <Field label="Result">
          <StatusBadge status={screening.status} />
        </Field>
        <Field label="Decided">{`${formatTimestamp(screening.decidedAt)} · ${screening.automatic ? "automatic" : "manual"}`}</Field>
        <Field label="Rule version">{screening.ruleVersion}</Field>
      </dl>
      {screening.reasons?.length ? (
        <ul className="da-reasons">
          {screening.reasons.map((reason) => (
            <li key={reason.code} className={`da-reason da-reason-${reason.outcome}`}>
              <span className={`ref-badge da-outcome-${reason.outcome}`}>{OUTCOME_LABELS[reason.outcome] || reason.outcome}</span>
              <div>
                <div className="ref-org-name">{reason.label}</div>
                <div className="ref-muted da-code">{reason.code}</div>
                {reason.evidence ? <div className="da-evidence">{typeof reason.evidence === "string" ? reason.evidence : JSON.stringify(reason.evidence)}</div> : null}
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="ref-muted">No rules matched.</p>
      )}
      {screening.questions?.length ? (
        <>
          <h4 className="da-subtitle">Outstanding questions</h4>
          <ul className="da-questions">
            {screening.questions.map((question) => (
              <li key={question}>{question}</li>
            ))}
          </ul>
        </>
      ) : null}
    </Section>
  );
}

function actorLabel(actor) {
  if (actor?.type === "admin") {
    return `Admin ${actor.uid || ""}`.trim();
  }

  return actor?.type === "applicant" ? "Applicant" : "System";
}

function HistorySection({ decisions, auditLog }) {
  return (
    <Section title="Screening history">
      {decisions.length ? (
        <table className="ref-table da-subtable">
          <thead>
            <tr>
              <th>When</th>
              <th>Decision</th>
              <th>By</th>
              <th>Reasons</th>
            </tr>
          </thead>
          <tbody>
            {decisions.map((decision) => (
              <tr key={decision.id}>
                <td>{formatTimestamp(decision.decidedAt)}</td>
                <td>
                  <StatusBadge status={decision.status} />
                </td>
                <td>
                  {decision.automatic ? "System" : actorLabel(decision.decidedBy)}
                  <div className="ref-muted da-code">{decision.ruleVersion}</div>
                </td>
                <td>
                  {(decision.reasons || []).map((reason) => reason.label || reason.code).join(", ") || "—"}
                  {decision.note ? <div className="ref-muted">Note: {decision.note}</div> : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="ref-muted">No decisions yet.</p>
      )}
      <h4 className="da-subtitle">Audit log</h4>
      {auditLog.length ? (
        <ul className="da-audit">
          {auditLog.map((entry) => (
            <li key={entry.id}>
              <span className="da-audit-time">{formatTimestamp(entry.timestamp)}</span>
              <span className="ref-org-name">{entry.action}</span>
              <span className="ref-muted">{actorLabel(entry.actor)}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="ref-muted">No audit entries.</p>
      )}
    </Section>
  );
}

export function ApplicantDetail({ env, applicantId, onClose, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);
  const [actionKey, setActionKey] = useState(null);
  const [isActionBusy, setIsActionBusy] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      setDetail(await getApplicant(env, applicantId));
    } catch (loadError) {
      setError(errorMessage(loadError));
    }
  }, [env, applicantId]);

  useEffect(() => {
    setDetail(null);
    load();
  }, [load]);

  const runAction = async ({ text }) => {
    const action = ACTIONS[actionKey];
    setIsActionBusy(true);
    try {
      const response = await action.run(env, applicantId, text);
      toast.success(action.success);
      setActionKey(null);
      onChanged?.(response.applicant);
      await load();
    } catch (actionError) {
      toast.error(errorMessage(actionError));
    } finally {
      setIsActionBusy(false);
    }
  };

  const applicant = detail?.applicant;
  const isClosed = applicant?.status === "revoked";
  const canDecide = applicant && !isClosed;

  return (
    <>
      <div className="da-drawer-backdrop" role="dialog" aria-modal="true" aria-labelledby="da-drawer-title" onClick={onClose}>
        <aside className="da-drawer" onClick={(event) => event.stopPropagation()}>
          <div className="da-drawer-header">
            <div>
              <p className="ref-eyebrow">Applicant</p>
              <h2 id="da-drawer-title" className="ref-section-title">
                {applicant?.profile?.fullName || applicant?.linkedin?.name || "Loading…"}
              </h2>
              {applicant ? <StatusBadge status={applicant.status} /> : null}
            </div>
            <button type="button" className="ref-btn ref-btn-ghost ref-btn-sm" onClick={onClose}>
              Close
            </button>
          </div>

          {error ? <div className="ref-error" role="alert">{error}</div> : null}

          {applicant ? (
            <>
              <div className="ref-row-actions da-actions">
                <button type="button" className="ref-btn ref-btn-primary ref-btn-sm" disabled={!canDecide || applicant.status === "approved"} onClick={() => setActionKey("approve")}>
                  Approve
                </button>
                <button type="button" className="ref-btn ref-btn-danger ref-btn-sm" disabled={!canDecide || applicant.status === "rejected"} onClick={() => setActionKey("reject")}>
                  Reject
                </button>
                <button type="button" className="ref-btn ref-btn-danger ref-btn-sm" disabled={isClosed} onClick={() => setActionKey("revoke")}>
                  Revoke
                </button>
                <button type="button" className="ref-btn ref-btn-ghost ref-btn-sm" disabled={!canDecide} onClick={() => setActionKey("requestInfo")}>
                  Request information
                </button>
                <button type="button" className="ref-btn ref-btn-ghost ref-btn-sm" disabled={!canDecide || !applicant.workEmailVerifiedAt} onClick={() => setActionKey("retry")}>
                  Re-run check
                </button>
              </div>
              {applicant.infoRequestMessage ? <p className="ref-preview-note">Information requested: {applicant.infoRequestMessage}</p> : null}
              <IdentitySection applicant={applicant} />
              <WebCheckSection applicant={applicant} />
              <ScreeningSection screening={applicant.screening} />
              <HistorySection decisions={detail.decisions || []} auditLog={detail.auditLog || []} />
            </>
          ) : null}
          {!applicant && !error ? <div className="ref-empty">Loading applicant…</div> : null}
        </aside>
      </div>
      <ConfirmModal action={actionKey ? ACTIONS[actionKey] : null} isBusy={isActionBusy} onConfirm={runAction} onCancel={() => setActionKey(null)} />
    </>
  );
}
